import React, { useState, useEffect, useRef } from 'react';
import { pipelineSteps } from '../data/mockData';
import { FileText, Search, SlidersHorizontal, Brain } from 'lucide-react';

const iconMap = {
  FileText: FileText,
  Search: Search,
  SlidersHorizontal: SlidersHorizontal,
  Brain: Brain,
};

const PipelineSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="funkciok" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <span className="text-xs font-bold tracking-[0.2em] text-blue-600 uppercase">
            Hogyan működik
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Az Ajánlási Pipeline
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Négy lépésben a katalógusból személyre szabott, indokolt ajánlásig.
          </p>
        </div>

        <div ref={sectionRef}>

        {/* Steps — mobile timeline */}
        <div className="md:hidden space-y-0">
          {pipelineSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <div
                key={`m-${step.step}`}
                className="flex gap-4"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
                  transition: `opacity 0.7s ease ${index * 0.18}s, transform 0.7s ease ${index * 0.18}s`,
                }}
              >
                {/* Left rail */}
                <div className="flex flex-col items-center flex-shrink-0 w-10">
                  <div className="w-10 h-10 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/25 flex-shrink-0 z-10">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {index < pipelineSteps.length - 1 && (
                    <div className="w-px flex-1 bg-gradient-to-b from-blue-300 to-blue-100 my-2" style={{ minHeight: 32 }} />
                  )}
                </div>
                {/* Content */}
                <div className={`flex-1 ${index < pipelineSteps.length - 1 ? 'pb-8' : 'pb-2'}`}>
                  <span className="text-[10px] font-bold tracking-[0.18em] text-blue-500 uppercase">{step.step}. Lépés</span>
                  <h3 className="text-base font-bold text-gray-900 mt-0.5 mb-1.5">{step.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Steps — desktop grid */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-5">
          {pipelineSteps.map((step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <div
                key={step.step}
                className="relative flex flex-col"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateX(0)' : 'translateX(-60px)',
                  transition: `opacity 0.9s ease ${index * 0.3}s, transform 0.9s ease ${index * 0.3}s`,
                }}
              >
                <div className="p-6 lg:p-8 bg-white border border-gray-100 rounded-2xl hover:shadow-lg hover:shadow-blue-50 transition-shadow duration-300 h-full flex flex-col">
                  {/* Icon + Step */}
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-xs font-bold tracking-[0.15em] text-blue-600 uppercase">
                      {step.step}. Lépés
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-500 leading-relaxed flex-1">
                    {step.description}
                  </p>
                </div>

                {/* Connector dot between cards */}
                {index < 3 && (
                  <div
                    className="hidden lg:flex absolute top-1/2 -right-[14px] transform -translate-y-1/2 items-center z-10"
                    style={{
                      opacity: isVisible ? 1 : 0,
                      transition: `opacity 0.4s ease ${1.0 + index * 0.3}s`,
                    }}
                  >
                    <div className="w-1.5 h-0.5 bg-blue-200" />
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-500 border-2 border-white shadow-sm" />
                    <div className="w-1.5 h-0.5 bg-blue-200" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </section>
  );
};

export default PipelineSection;
